import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, CheckCircle, XCircle, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { AdminBookingFilters } from '../components/AdminBookingFilters';
import { AdminBookingTable } from '../components/AdminBookingTable';
import { AdminBookingDetailsModal } from '../components/AdminBookingDetailsModal';
import { CancelBookingModal } from '../components/CancelBookingModal';
import { StatsCard } from '../components/StatsCard';
import {
  getAdminBookingsList,
  getAdminBookingDetails,
  updateAdminBookingStatus,
  cancelAdminBooking,
} from '../services/api';
import { AdminBookingListItem, AdminBookingStatus } from '../types';

const PAGE_LIMIT = 10;

export const AdminBookingsPage: React.FC = () => {
  const [bookings, setBookings] = useState<AdminBookingListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [statusFilter, setStatusFilter] = useState<AdminBookingStatus | 'all'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState<number | null>(null);

  const fetchBookings = useCallback(async (currentPage: number) => {
    try {
      setLoading(true);
      setError('');

      const response = await getAdminBookingsList({
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo }),
        ...(searchQuery.trim() && { search: searchQuery.trim() }),
        page: currentPage,
        limit: PAGE_LIMIT,
      });

      if (response.success) {
        setBookings(response.bookings);
        setTotal(response.total ?? response.bookings.length);
      } else {
        setError(response.error || 'Failed to fetch bookings');
      }
    } catch (err) {
      setError('An error occurred while fetching bookings');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dateFrom, dateTo, searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, dateFrom, dateTo, searchQuery]);

  useEffect(() => {
    fetchBookings(page);
  }, [fetchBookings, page]);

  const handleViewDetails = async (bookingId: number) => {
    try {
      setActionLoading(true);
      const response = await getAdminBookingDetails({ bookingId });

      if (response.success) {
        setSelectedBooking(response.booking);
        setShowDetailsModal(true);
      } else {
        setError(response.error || 'Failed to fetch booking details');
      }
    } catch (err) {
      setError('An error occurred while fetching booking details');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateStatus = async (bookingId: number, status: AdminBookingStatus) => {
    if (status === 'cancelled') {
      setBookingToCancel(bookingId);
      setShowCancelModal(true);
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      setSuccess('');

      const response = await updateAdminBookingStatus({ bookingId, status });

      if (response.success) {
        setSuccess(`Booking status updated to ${status}`);
        setShowDetailsModal(false);
        fetchBookings(page);
      } else {
        setError(response.error || 'Failed to update booking status');
      }
    } catch (err) {
      setError('An error occurred while updating booking status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelBooking = async (reason: string) => {
    if (!bookingToCancel) return;

    try {
      setActionLoading(true);
      setError('');
      setSuccess('');

      const response = await cancelAdminBooking({
        bookingId: bookingToCancel,
        reason,
      });

      if (response.success) {
        setSuccess('Booking cancelled successfully');
        setShowCancelModal(false);
        setShowDetailsModal(false);
        setBookingToCancel(null);
        fetchBookings(page);
      } else {
        setError(response.error || 'Failed to cancel booking');
      }
    } catch (err) {
      setError('An error occurred while cancelling booking');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClearFilters = () => {
    setStatusFilter('all');
    setDateFrom('');
    setDateTo('');
    setSearchQuery('');
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));
  const pageStart = total === 0 ? 0 : (page - 1) * PAGE_LIMIT + 1;
  const pageEnd = Math.min(page * PAGE_LIMIT, total);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Bookings Management</h1>
          <p className="text-gray-600">
            View and manage all bookings in the system
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <StatsCard
            title="Total Bookings"
            value={total}
            icon={Calendar}
            color="slate"
          />
          <StatsCard
            title="Pending"
            value={bookings.filter((b) => b.status === 'pending').length}
            icon={Clock}
            color="yellow"
          />
          <StatsCard
            title="Confirmed"
            value={bookings.filter((b) => b.status === 'confirmed' || b.status === 'accepted').length}
            icon={CheckCircle}
            color="slate"
          />
          <StatsCard
            title="Completed"
            value={bookings.filter((b) => b.status === 'completed').length}
            icon={CheckCircle}
            color="green"
          />
          <StatsCard
            title="Cancelled"
            value={bookings.filter((b) => b.status === 'cancelled' || b.status === 'rejected').length}
            icon={XCircle}
            color="red"
          />
        </div>

        <AdminBookingFilters
          statusFilter={statusFilter}
          dateFrom={dateFrom}
          dateTo={dateTo}
          searchQuery={searchQuery}
          onStatusFilterChange={setStatusFilter}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onSearchChange={setSearchQuery}
          onClearFilters={handleClearFilters}
        />

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Bookings List
              </h2>
              <span className="text-sm text-gray-500">
                {total === 0
                  ? 'No bookings found'
                  : `Showing ${pageStart}–${pageEnd} of ${total} bookings`}
              </span>
            </div>
          </div>

          <AdminBookingTable
            bookings={bookings}
            loading={loading}
            onViewDetails={handleViewDetails}
          />

          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-500">
                Page {page} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1 || loading}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        disabled={loading}
                        className={`w-9 h-9 text-sm font-medium rounded-lg transition-colors disabled:cursor-not-allowed ${
                          pageNum === page
                            ? 'bg-blue-600 text-white'
                            : 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages || loading}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          )}
        </div>

        <AdminBookingDetailsModal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedBooking(null);
          }}
          booking={selectedBooking}
          onUpdateStatus={handleUpdateStatus}
          onCancel={(bookingId) => {
            setBookingToCancel(bookingId);
            setShowCancelModal(true);
          }}
          loading={actionLoading}
        />

        <CancelBookingModal
          isOpen={showCancelModal}
          onClose={() => {
            setShowCancelModal(false);
            setBookingToCancel(null);
          }}
          onConfirm={handleCancelBooking}
          bookingId={bookingToCancel || 0}
          loading={actionLoading}
        />
      </div>
    </div>
  );
};
